import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { post } from '@/lib/api';
import { Input } from '@/components/ui/input';
import {
  createTextZone, createLogoZone, zoneBox, setZoneBox, pointInBox, setAnchor, setPad, setTracking,
  listZones, snapZoneToElement, alignBoxes, distributeBoxes, type Box, type Align,
  setColorRole, setFont, setEffect, serializeClean, summarizeSlots, topComponent, rootBBox,
  detectColorsInRoot, bulkTagColor, getStrokes, setStrokes, getArchCenter, setArchCenter,
  SLOT_TYPES, SLOT_LABELS, INSTALLED_FONTS, EFFECTS, type StrokeSpec,
} from '@/lib/svg-edit';

// New Design — ZONE-FIRST template authoring. Upload any artwork SVG, then DRAW a
// box on the canvas to place a Text zone (Team Name / Mascot / Sport / Year …) or a
// Logo zone. Move/resize zones, multi-select to align & distribute them, and tune
// each zone's role / font / effect / anchor. The renderer (sport-templates) fits a
// school's data into every zone at render time; this editor only authors the layout.

const SVGNS = 'http://www.w3.org/2000/svg';
const SAMPLE = {
  colorway: ['royal', 'gold', 'white'],
  team_text: 'RIVERSIDE', mascot_text: 'TIGERS', school_text: 'RIVERSIDE HIGH', sport: 'FOOTBALL',
  year: '2026', year_y1y2: '20', year_yy: '26', school_initials: 'RHS', number: '24',
  player_name: 'SMITH', last_name: 'SMITH', coach_name: 'COACH J', league_text: 'METRO LEAGUE',
  tournament_text: 'STATE FINALS', city_text: 'RIVERSIDE', tagline: 'GO BIG OR GO HOME',
};

const COLOR_SLOTS = [
  { value: 'primary', label: 'Primary (1)' },
  { value: 'secondary', label: 'Secondary (2)' },
  { value: 'tertiary', label: 'Tertiary (3)' },
  { value: 'accent', label: 'Accent (4)' },
];

// Anchor grid (matches setAnchor codes). Row-major: tl,tc,tr / cl,cm,cr / bl,bc,br.
const ANCHORS: { code: string; title: string }[] = [
  { code: 'tl', title: 'Top-left' }, { code: 'tc', title: 'Top-center' }, { code: 'tr', title: 'Top-right' },
  { code: 'cl', title: 'Middle-left' }, { code: 'cm', title: 'Middle-center' }, { code: 'cr', title: 'Middle-right' },
  { code: 'bl', title: 'Bottom-left' }, { code: 'bc', title: 'Bottom-center' }, { code: 'br', title: 'Bottom-right' },
];

// 8 resize handles + the local anchor corner they pin against (the opposite corner stays fixed).
type HandleId = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
const HANDLES: HandleId[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const HANDLE_CURSOR: Record<HandleId, string> = {
  nw: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize',
  n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize',
};

function clientToVb(r: SVGSVGElement, cx: number, cy: number) {
  const pt = r.createSVGPoint(); pt.x = cx; pt.y = cy;
  const m = r.getScreenCTM(); if (!m) return { x: 0, y: 0 };
  const p = pt.matrixTransform(m.inverse());
  return { x: p.x, y: p.y };
}
const normBox = (a: { x: number; y: number }, b: { x: number; y: number }): Box =>
  ({ x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) });

function renderPreviewReq(svgMarkup: string): Promise<string> {
  return fetch('/api/recipes/render-preview', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ svgMarkup, schoolData: SAMPLE, format: 'svg' }),
  }).then((r) => { if (!r.ok) throw new Error(`render ${r.status}`); return r.text(); });
}

function PreviewModal({ svg, onClose }: { svg: string; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const host = ref.current; if (!host) return;
    try {
      const doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
      const el = document.importNode(doc.documentElement, true) as unknown as SVGSVGElement;
      el.setAttribute('style', 'width:100%;height:100%');
      host.replaceChildren(el);
    } catch { /* ignore */ }
  }, [svg]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="rounded-md bg-white p-4 shadow-xl" style={{ width: 560, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[12px] font-semibold">Preview — sample school (Riverside Tigers)</span>
          <button onClick={onClose} className="text-[12px] text-muted hover:text-text">Close ✕</button>
        </div>
        <div ref={ref} className="aspect-square w-full rounded-sm border border-border" />
        <p className="mt-2 text-[10.5px] text-subtle">Sample colors + text. Each zone is filled with the school's data — gray boxes are logo zones.</p>
      </div>
    </div>
  );
}

export default function TemplateAuthor() {
  const canvasRef = useRef<HTMLDivElement>(null);
  const originalRef = useRef<string>('');
  const historyRef = useRef<string[]>([]);
  const [selected, setSelected] = useState<SVGGraphicsElement | null>(null);
  const [multi, setMulti] = useState<SVGElement[]>([]);
  const [version, setVersion] = useState(0);
  const [hasSvg, setHasSvg] = useState(false);
  const [status, setStatus] = useState('Upload an artwork SVG to begin.');
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [sport, setSport] = useState('football');
  const [preview, setPreview] = useState<string | null>(null);
  const [drawMode, setDrawMode] = useState<null | 'text' | 'logo'>(null);
  const [slotType, setSlotType] = useState('team_text');
  const [font, setFontSel] = useState('Anton');
  const [effect, setEffectSel] = useState('straight');
  const [distMode, setDistMode] = useState<'gaps' | 'centers'>('gaps');

  // Drag state (refs so pointer handlers see live values without re-binding).
  const dragRef = useRef<null | 'draw' | 'move' | 'resize'>(null);
  const dragStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const dragBoxRef = useRef<Box>({ x: 0, y: 0, w: 0, h: 0 });
  const dragHandleRef = useRef<HandleId | null>(null);

  const root = (): SVGSVGElement | null => (canvasRef.current?.querySelector('svg') as SVGSVGElement | null) || null;
  const bump = () => setVersion((v) => v + 1);

  // viewBox width of the loaded artwork (canvas units), for the layout helpers.
  function canvasSize(r: SVGSVGElement): number {
    const vb = r.getAttribute('viewBox');
    if (vb) { const p = vb.trim().split(/[\s,]+/).map(Number); if (p.length === 4 && p[2] > 0) return p[2]; }
    const w = parseFloat(r.getAttribute('width') || ''); if (Number.isFinite(w) && w > 0) return w;
    return 1000;
  }

  // ─── Overlays: drawn by appending to the live <svg>, stripped before serialize ──
  // Editor-only nodes: temp draw rect (#__draw), selection box (#__selbox), the
  // multi-select outline (#__mbox), and the 8 resize handles (#__h-*). None carry
  // data-slot/data-color, so summarizeSlots/listZones ignore them; we strip them
  // before any serialize and before the color scan so their fills can't leak in.
  const OVERLAY_SEL = '#__draw, #__selbox, #__mbox, [id^="__h-"]';
  function serialize(r: SVGSVGElement): string {
    r.querySelectorAll(OVERLAY_SEL).forEach((n) => n.remove());
    return serializeClean(r);
  }

  function rectOverlay(id: string, b: { x: number; y: number; width: number; height: number }, stroke: string, dash?: string, fill = 'none') {
    const box = document.createElementNS(SVGNS, 'rect');
    box.setAttribute('id', id);
    box.setAttribute('x', String(b.x)); box.setAttribute('y', String(b.y));
    box.setAttribute('width', String(b.width)); box.setAttribute('height', String(b.height));
    box.setAttribute('fill', fill); box.setAttribute('stroke', stroke); box.setAttribute('stroke-width', '2');
    box.setAttribute('vector-effect', 'non-scaling-stroke');
    if (dash) box.setAttribute('stroke-dasharray', dash);
    box.setAttribute('pointer-events', 'none');
    return box;
  }

  // Box to outline, in ROOT viewBox units. For a zone, use its authored zone box
  // (the rect the rep drew) so handles track the box — not the fitted glyph bbox.
  // For raw artwork elements, fall back to the rendered bounding box.
  function overlayBox(el: SVGElement): { x: number; y: number; width: number; height: number } {
    const r = root();
    if (el.hasAttribute('data-slot')) { const z = zoneBox(el); return { x: z.x, y: z.y, width: z.w, height: z.h }; }
    return rootBBox(el as SVGGraphicsElement, r!);
  }

  // Selection box + 8 corner/edge handles for the currently-selected zone, plus a
  // faint outline on every other multi-selected zone. Re-run on each render.
  function drawOverlays() {
    const r = root(); if (!r) return;
    r.querySelectorAll(OVERLAY_SEL).forEach((n) => n.remove());
    // multi-select outlines (skip the primary selection — it gets the full selbox)
    for (const el of multi) {
      if (el === selected) continue;
      try {
        const b = overlayBox(el);
        r.appendChild(rectOverlay('__mbox', { x: b.x - 2, y: b.y - 2, width: b.width + 4, height: b.height + 4 }, '#2f6bff', '4 3'));
      } catch { /* ignore */ }
    }
    if (!selected) return;
    try {
      const b = overlayBox(selected);
      r.appendChild(rectOverlay('__selbox', { x: b.x - 2, y: b.y - 2, width: b.width + 4, height: b.height + 4 }, '#2f6bff', '6 4'));
      // handles sized in viewBox units relative to artwork extent so they stay grabbable
      const hs = Math.max(6, canvasSize(r) * 0.012);
      const cx = b.x + b.width / 2, cy = b.y + b.height / 2;
      const pos: Record<HandleId, { x: number; y: number }> = {
        nw: { x: b.x, y: b.y }, n: { x: cx, y: b.y }, ne: { x: b.x + b.width, y: b.y },
        e: { x: b.x + b.width, y: cy }, se: { x: b.x + b.width, y: b.y + b.height }, s: { x: cx, y: b.y + b.height },
        sw: { x: b.x, y: b.y + b.height }, w: { x: b.x, y: cy },
      };
      for (const h of HANDLES) {
        const p = pos[h];
        const rc = document.createElementNS(SVGNS, 'rect');
        rc.setAttribute('id', `__h-${h}`);
        rc.setAttribute('x', String(p.x - hs / 2)); rc.setAttribute('y', String(p.y - hs / 2));
        rc.setAttribute('width', String(hs)); rc.setAttribute('height', String(hs));
        rc.setAttribute('fill', '#fff'); rc.setAttribute('stroke', '#2f6bff'); rc.setAttribute('stroke-width', '1.5');
        rc.setAttribute('vector-effect', 'non-scaling-stroke');
        rc.setAttribute('style', `cursor:${HANDLE_CURSOR[h]}`);
        r.appendChild(rc);
      }
    } catch { /* ignore bbox failures */ }
  }

  // Redraw overlays synchronously after every render (before paint) so the
  // render-body overlay strip used for color detection never flickers on screen.
  useLayoutEffect(() => { if (hasSvg) drawOverlays(); }); // eslint-disable-line react-hooks/exhaustive-deps

  function injectSvg(svgEl: Element) {
    const el = document.importNode(svgEl, true) as unknown as SVGSVGElement;
    el.setAttribute('style', 'width:100%;height:100%;display:block');
    if (!el.getAttribute('xmlns')) el.setAttribute('xmlns', SVGNS);
    canvasRef.current?.replaceChildren(el);
  }

  async function loadFile(file: File) {
    const text = await file.text();
    const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
    if (doc.documentElement.nodeName.toLowerCase() !== 'svg') { setStatus('That file is not a valid SVG.'); return; }
    injectSvg(doc.documentElement);
    const r = root();
    originalRef.current = r ? serialize(r) : '';
    historyRef.current = [];
    setSelected(null); setMulti([]); setDrawMode(null); setHasSvg(true);
    const base = file.name.replace(/\.svg$/i, '');
    if (!name) setName(base);
    if (!id) setId(base.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
    setStatus('Draw a Text or Logo zone on the artwork, or map its colors →');
    bump();
  }

  function snapshot() { const r = root(); if (r) historyRef.current.push(serialize(r)); }

  // ─── Pointer interaction: draw-to-create · move · resize · select ─────────────
  function handleAt(t: Element): HandleId | null {
    const id = (t as Element).id || '';
    const m = id.match(/^__h-(nw|n|ne|e|se|s|sw|w)$/);
    return m ? (m[1] as HandleId) : null;
  }
  // Is `t` the selected zone (or inside its subtree)?
  function isInSelected(t: Element): boolean {
    if (!selected) return false;
    return t === selected || selected.contains(t);
  }

  function onPointerDown(e: React.PointerEvent) {
    const r = root(); if (!r) return;
    const t = e.target as Element;
    const here = clientToVb(r, e.clientX, e.clientY);

    // 1) Draw-to-create when a draw mode is armed.
    if (drawMode) {
      dragRef.current = 'draw';
      dragStartRef.current = here;
      const temp = rectOverlay('__draw', { x: here.x, y: here.y, width: 0, height: 0 }, '#2f6bff', '6 4', 'rgba(47,107,255,.12)');
      r.appendChild(temp);
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
      e.preventDefault();
      return;
    }

    // 2) Resize the selected zone via a handle.
    const handle = handleAt(t);
    if (handle && selected) {
      snapshot();
      dragRef.current = 'resize';
      dragHandleRef.current = handle;
      dragBoxRef.current = zoneBox(selected as SVGElement);
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
      e.preventDefault();
      return;
    }

    // 2.5) Grab anywhere inside the SELECTED zone's box to move it — the ENTIRE
    // box is draggable, not just its text glyph (step 4 below) or the resize
    // handles (step 2 above, checked first so corner grabs still resize). Pure
    // geometric hit-test in viewBox units — no overlay node, so there's nothing
    // extra to strip before serialize / preview / save / color-scan.
    if (!e.shiftKey && selected && selected.hasAttribute('data-slot') && pointInBox(here, zoneBox(selected as SVGElement))) {
      snapshot();
      dragRef.current = 'move';
      dragStartRef.current = here;
      dragBoxRef.current = zoneBox(selected as SVGElement);
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
      e.preventDefault();
      return;
    }

    // 3) Empty space → deselect.
    if (!t || t === r || t === canvasRef.current || (t as Element).id === '__selbox' || (t as Element).id === '__mbox') {
      setSelected(null); setMulti([]); bump(); return;
    }

    // 4) A zone (or artwork element) was clicked → select it.
    const comp = topComponent(t, r) as SVGGraphicsElement;
    const isZone = comp.hasAttribute('data-slot');

    // Shift-click toggles multi-select membership (zones only).
    if (e.shiftKey && isZone) {
      setMulti((prev) => prev.includes(comp) ? prev.filter((z) => z !== comp) : [...prev, comp]);
      setSelected(comp);
      bump();
      return;
    }

    // Plain click on an already-selected zone (or its subtree) → start moving it.
    if (isZone && (comp === selected || isInSelected(t))) {
      snapshot();
      dragRef.current = 'move';
      dragStartRef.current = here;
      dragBoxRef.current = zoneBox(selected as SVGElement);
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
      e.preventDefault();
      return;
    }

    // Otherwise select it (single).
    setSelected(comp);
    setMulti(isZone ? [comp] : []);
    bump();
  }

  function onPointerMove(e: React.PointerEvent) {
    const r = root(); const mode = dragRef.current; if (!r || !mode) return;
    const here = clientToVb(r, e.clientX, e.clientY);

    if (mode === 'draw') {
      const b = normBox(dragStartRef.current, here);
      const temp = r.querySelector('#__draw');
      if (temp) { temp.setAttribute('x', String(b.x)); temp.setAttribute('y', String(b.y)); temp.setAttribute('width', String(b.w)); temp.setAttribute('height', String(b.h)); }
      return;
    }

    if (mode === 'move' && selected) {
      const dx = here.x - dragStartRef.current.x, dy = here.y - dragStartRef.current.y;
      const b = dragBoxRef.current;
      setZoneBox(selected as SVGElement, { x: b.x + dx, y: b.y + dy, w: b.w, h: b.h });
      drawOverlays();
      return;
    }

    if (mode === 'resize' && selected) {
      const start = dragBoxRef.current; const h = dragHandleRef.current!;
      // Fixed anchor = opposite corner/edge; recompute box from anchor + current point.
      let x1 = start.x, y1 = start.y, x2 = start.x + start.w, y2 = start.y + start.h;
      if (h.includes('w')) x1 = here.x; if (h.includes('e')) x2 = here.x;
      if (h.includes('n')) y1 = here.y; if (h.includes('s')) y2 = here.y;
      const nb: Box = { x: Math.min(x1, x2), y: Math.min(y1, y2), w: Math.max(1, Math.abs(x2 - x1)), h: Math.max(1, Math.abs(y2 - y1)) };
      setZoneBox(selected as SVGElement, nb);
      drawOverlays();
      return;
    }
  }

  function onPointerUp(e: React.PointerEvent) {
    const r = root(); const mode = dragRef.current; if (!r || !mode) { dragRef.current = null; return; }
    (e.currentTarget as Element).releasePointerCapture?.(e.pointerId);

    if (mode === 'draw') {
      const here = clientToVb(r, e.clientX, e.clientY);
      const b = normBox(dragStartRef.current, here);
      r.querySelector('#__draw')?.remove();
      dragRef.current = null;
      if (b.w >= 10 && b.h >= 10) {
        snapshot();
        const el = drawMode === 'logo' ? createLogoZone(r, b) : createTextZone(r, b, slotType, font);
        if (drawMode === 'text' && effect !== 'straight') setEffect(el, effect);
        setSelected(el as SVGGraphicsElement); setMulti([el]);
        setStatus(drawMode === 'logo' ? '✓ Logo zone added.' : `✓ ${slotType.replace(/_/g, ' ')} zone added.`);
      } else {
        setStatus('Box too small — drag a larger area.');
      }
      setDrawMode(null);
      bump();
      return;
    }

    // move / resize finished
    dragRef.current = null; dragHandleRef.current = null;
    bump();
  }

  // ─── Zone inspector actions ───────────────────────────────────────────────────
  const selSlot = selected ? (selected.getAttribute('data-slot') || '') : '';
  const isTextZone = !!selected && selSlot !== '' && selSlot !== 'logo_zone';
  const isLogoZone = !!selected && selSlot === 'logo_zone';
  const isZone = isTextZone || isLogoZone;

  function changeRole(newSlot: string) {
    if (!isTextZone || !selected) return; snapshot();
    const oldDef = SLOT_TYPES.find((s) => s.value === selSlot);
    const newDef = SLOT_TYPES.find((s) => s.value === newSlot);
    selected.setAttribute('data-slot', newSlot);
    selected.textContent = SLOT_LABELS[newSlot] || newSlot.replace(/_/g, ' ').toUpperCase();
    // Move the color to the new role's default ONLY if it still sits on the old
    // role's default (i.e. the rep hasn't picked a custom color slot).
    const cur = selected.getAttribute('data-color') || '';
    if (newDef && (!cur || (oldDef && cur === oldDef.role))) setColorRole(selected, newDef.role);
    setSlotType(newSlot);
    bump(); setStatus(`✓ Role → ${newSlot.replace(/_/g, ' ')}`);
  }
  function applyFont(f: string) {
    setFontSel(f);
    if (isTextZone && selected) { snapshot(); setFont(selected, f); bump(); setStatus(`✓ Font → ${f}`); }
  }
  function applyEffect(eff: string) {
    setEffectSel(eff);
    if (isTextZone && selected) { snapshot(); setEffect(selected, eff); bump(); setStatus(`✓ Effect → ${eff}`); }
  }
  function applyArchCenter(c: number) {
    if (!isTextZone || !selected) return; snapshot(); setArchCenter(selected, c); bump();
  }
  function applyColor(role: string) {
    if (!isTextZone || !selected) return; snapshot(); setColorRole(selected, role); bump(); setStatus(`✓ Color slot → ${role}.`);
  }
  function applyAnchor(code: string) {
    if (!isZone || !selected) return; snapshot(); setAnchor(selected as SVGElement, code); bump(); setStatus(`✓ Anchor → ${code}`);
  }
  function applyPad(pad: number) {
    if (!isLogoZone || !selected) return; snapshot(); setPad(selected as SVGElement, pad); bump();
  }
  function applyTracking(t: number) {
    if (!isTextZone || !selected) return; snapshot(); setTracking(selected as SVGElement, t); bump();
  }
  function addStroke() {
    if (!isTextZone || !selected) return; snapshot();
    const cur = getStrokes(selected); cur.push({ color: 'secondary', width: 10 });
    setStrokes(selected, cur); bump(); setStatus('✓ Outline added.');
  }
  function updateStroke(i: number, patch: Partial<StrokeSpec>) {
    if (!isTextZone || !selected) return; snapshot();
    const cur = getStrokes(selected); cur[i] = { ...cur[i], ...patch };
    setStrokes(selected, cur); bump();
  }
  function removeStroke(i: number) {
    if (!isTextZone || !selected) return; snapshot();
    const cur = getStrokes(selected); cur.splice(i, 1);
    setStrokes(selected, cur); bump(); setStatus('✓ Outline removed.');
  }
  function assignDetectedColor(color: string, role: string) {
    const r = root(); if (!r) return; snapshot();
    const n = bulkTagColor(r, color, role); bump();
    setStatus(`✓ ${n} region${n === 1 ? '' : 's'} of ${color} → ${role}`);
  }
  // Turn a clicked non-zone artwork element into a text zone at its bounding box.
  function snapSelectionToZone() {
    const r = root(); if (!r || !selected) return; snapshot();
    const el = snapZoneToElement(selected, r, slotType, font);
    if (effect !== 'straight') setEffect(el, effect);
    setSelected(el as SVGGraphicsElement); setMulti([el]); bump();
    setStatus(`✓ Selection → ${slotType.replace(/_/g, ' ')} zone.`);
  }

  function selectZone(el: SVGElement, additive = false) {
    if (additive) {
      setMulti((prev) => prev.includes(el) ? prev.filter((z) => z !== el) : [...prev, el]);
      setSelected(el as SVGGraphicsElement);
    } else {
      setSelected(el as SVGGraphicsElement); setMulti([el]);
    }
    bump();
  }
  function deleteZone(el: SVGElement) {
    const r = root(); if (!r) return; snapshot();
    el.remove();
    if (selected === el) setSelected(null);
    setMulti((prev) => prev.filter((z) => z !== el));
    bump(); setStatus('Zone deleted.');
  }
  function del() { if (selected) deleteZone(selected); }

  function undo() {
    const prev = historyRef.current.pop();
    if (!prev) { setStatus('Nothing to undo.'); return; }
    const doc = new DOMParser().parseFromString(prev, 'image/svg+xml');
    injectSvg(doc.documentElement); setSelected(null); setMulti([]); bump(); setStatus('Undid last change.');
  }
  function reset() {
    if (!originalRef.current) return;
    const doc = new DOMParser().parseFromString(originalRef.current, 'image/svg+xml');
    injectSvg(doc.documentElement); historyRef.current = []; setSelected(null); setMulti([]); bump(); setStatus('Reset to the uploaded artwork.');
  }

  // ─── Multi-select layout ──────────────────────────────────────────────────────
  function doAlign(how: Align) {
    const r = root(); if (!r || !multi.length) return; snapshot();
    const boxes = multi.map((el) => zoneBox(el));
    const out = alignBoxes(boxes, how, canvasSize(r));
    multi.forEach((el, i) => setZoneBox(el, out[i]));
    bump(); setStatus(`✓ Aligned ${how}.`);
  }
  function doDistribute(axis: 'h' | 'v') {
    const r = root(); if (!r || multi.length < 3) return; snapshot();
    const boxes = multi.map((el) => zoneBox(el));
    const out = distributeBoxes(boxes, axis, distMode);
    multi.forEach((el, i) => setZoneBox(el, out[i]));
    bump(); setStatus(`✓ Distributed ${axis === 'h' ? 'horizontally' : 'vertically'} (${distMode}).`);
  }

  async function doPreview() {
    const r = root(); if (!r) return;
    setStatus('Rendering preview…');
    try { setPreview(await renderPreviewReq(serialize(r))); setStatus(''); }
    catch (e) { setStatus('Preview failed: ' + (e as Error).message); }
  }

  async function save() {
    const r = root(); if (!r) { setStatus('Upload an SVG first.'); return; }
    if (!id || !name) { setStatus('Need a Template ID and Name.'); return; }
    if (listZones(r).length < 1) { setStatus('Draw at least one zone (Text or Logo) before saving.'); return; }
    const markup = serialize(r);
    const sum = summarizeSlots(r);
    setStatus('Saving…');
    try {
      await post('/api/recipes', { id, name, sport, svgMarkup: markup, slots: { textSlots: sum.textSlots, colorRoles: sum.colorRoles } });
      setStatus(`✓ Saved "${id}". It's now in Preview / Proof and Assign to Products.`);
    } catch (e) { setStatus('Save failed: ' + (e as Error).message); }
  }

  // ─── Derived view data (recomputed each render via `version`) ──────────────────
  const r = root();
  // Strip overlays before scanning so the handle/draw-rect fills don't pollute the
  // detected-colors list; the post-commit effect re-adds them.
  if (r) r.querySelectorAll(OVERLAY_SEL).forEach((n) => n.remove());
  const zones = hasSvg && r && version >= 0 ? listZones(r) : [];
  const detected = hasSvg && r ? detectColorsInRoot(r) : [];
  const curStrokes = isTextZone && selected ? getStrokes(selected) : [];
  const curArchCenter = isTextZone && selected ? getArchCenter(selected) : 0.5;
  const selEffect = selected ? (selected.getAttribute('data-effect') || 'straight') : 'straight';
  const selRole = selected ? (selected.getAttribute('data-color') || 'primary') : 'primary';
  const rawSelFont = (selected && selected.getAttribute('font-family')) || '';
  const selFont = INSTALLED_FONTS.includes(rawSelFont) ? rawSelFont : font;
  const selAnchor = selected ? (selected.getAttribute('data-anchor') || 'cm') : 'cm';
  const selTracking = selected && selected.getAttribute('data-tracking') ? Number(selected.getAttribute('data-tracking')) : 0;
  const selPad = selected && selected.getAttribute('data-pad') ? Number(selected.getAttribute('data-pad')) : 0;
  const selColorClicked = selected && !isZone; // a raw artwork element is selected (not yet a zone)
  const isArch = selEffect === 'arch_up' || selEffect === 'arch_down';

  const TBTN = (active: boolean) =>
    `rounded-sm border px-3 py-1.5 text-[11px] font-medium ${active ? 'border-primary bg-primary text-white' : 'border-border bg-white text-text hover:bg-bg'}`;

  return (
    <div className="px-7 py-6">
      {preview && <PreviewModal svg={preview} onClose={() => setPreview(null)} />}
      <h1 className="text-[20px] font-semibold tracking-tighter">New Design</h1>
      <p className="mb-4 text-[12px] text-muted">
        Upload artwork → draw a Text or Logo zone → set its role / font / effect → move, resize, and align zones → save.
      </p>

      <div className="grid items-start gap-5 lg:grid-cols-[1fr_360px]">
        {/* Canvas + toolbar */}
        <div className="rounded-md border border-border bg-white p-3 shadow-card">
          {/* Toolbar */}
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            <button disabled={!hasSvg} onClick={() => setDrawMode(drawMode === 'text' ? null : 'text')} className={TBTN(drawMode === 'text') + ' disabled:opacity-40'}>+ Text Zone</button>
            <button disabled={!hasSvg} onClick={() => setDrawMode(drawMode === 'logo' ? null : 'logo')} className={TBTN(drawMode === 'logo') + ' disabled:opacity-40'}>+ Logo Zone</button>
            {drawMode && (
              <span className="ml-1 inline-flex items-center gap-1 rounded-sm bg-primary/10 px-2 py-1 text-[10.5px] text-primary">Drag a box on the artwork
                <button onClick={() => setDrawMode(null)} className="text-primary/70 hover:text-primary">✕</button>
              </span>
            )}
            {hasSvg && (
              <div className="ml-auto flex items-center gap-1.5">
                <button onClick={undo} className="rounded-sm border border-border bg-white px-2.5 py-1.5 text-[11px] hover:bg-bg">↶ Undo</button>
                <button onClick={reset} className="rounded-sm border border-border bg-white px-2.5 py-1.5 text-[11px] hover:bg-bg">Reset</button>
              </div>
            )}
          </div>

          {/* canvasRef div is intentionally childless in JSX so React never reconciles
              the SVG we inject imperatively (avoids removeChild crashes). All overlays
              (temp draw rect, selbox, handles) are appended to the live <svg>. */}
          <div className="relative aspect-square w-full">
            <div
              ref={canvasRef}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              className={`absolute inset-0 flex items-center justify-center touch-none ${drawMode ? 'cursor-crosshair [&_svg_*]:cursor-crosshair' : '[&_svg_*]:cursor-pointer'}`}
            />
            {!hasSvg && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[12px] text-muted">Upload an SVG to start →</div>
            )}
          </div>
          <div className="mt-2 min-h-[16px] text-[11px] text-muted">{status}</div>
        </div>

        {/* Controls */}
        <div className="space-y-3">
          <div className="rounded-md bg-surface p-4 shadow-card">
            <label className="mb-1 block text-[11px] text-muted">Artwork SVG</label>
            <input
              type="file"
              accept=".svg,image/svg+xml"
              onChange={(e) => { const f = e.currentTarget.files?.[0]; if (f) loadFile(f); }}
              className="block w-full text-[11px] text-text file:mr-2 file:rounded-sm file:border file:border-border file:bg-white file:px-2 file:py-1 file:text-[11px]"
            />
          </div>

          {/* New-zone defaults (used by the toolbar draw buttons) */}
          {hasSvg && (
            <div className="rounded-md bg-surface p-4 shadow-card">
              <div className="mb-1 text-[11px] font-semibold text-text">New text-zone defaults</div>
              <div className="mb-2 text-[10px] text-subtle">Applied to the next text zone you draw (and editable per-zone afterward).</div>
              <select value={slotType} onChange={(e) => setSlotType(e.target.value)} className="mb-1.5 h-8 w-full rounded-sm border border-border bg-white px-1.5 text-[11px]">
                {SLOT_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-1.5">
                <select value={font} onChange={(e) => applyFont(e.target.value)} title="Font" className="h-8 rounded-sm border border-border bg-white px-1.5 text-[11px]">
                  {INSTALLED_FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <select value={effect} onChange={(e) => applyEffect(e.target.value)} title="Text effect" className="h-8 rounded-sm border border-border bg-white px-1.5 text-[11px]">
                  {EFFECTS.map((ef) => <option key={ef.value} value={ef.value}>{ef.label}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* Colors panel */}
          {hasSvg && detected.length > 0 && (
            <div className="rounded-md bg-surface p-4 shadow-card">
              <div className="mb-0.5 text-[11px] font-semibold text-text">Colors in this artwork</div>
              <div className="mb-2 text-[10px] text-subtle">Map each color to a colorway slot — every region of that color recolors per school at build time.</div>
              <div className="space-y-1.5">
                {detected.map((c) => (
                  <div key={c.color} className="flex items-center gap-2">
                    <span className="h-5 w-5 shrink-0 rounded-sm border border-border" style={{ background: c.color }} />
                    <span className="font-mono text-[10.5px] text-muted">{c.color}</span>
                    <span className="text-[10px] text-subtle">×{c.count}</span>
                    <select defaultValue="" onChange={(e) => { if (e.target.value) assignDetectedColor(c.color, e.target.value); }} className="ml-auto h-7 rounded-sm border border-border bg-white px-1.5 text-[11px]">
                      <option value="" disabled>Assign slot…</option>
                      {COLOR_SLOTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Zone inspector (single selection) */}
          {hasSvg && isZone && (
            <div className="rounded-md bg-surface p-4 shadow-card">
              <div className="mb-2 text-[11px] font-semibold text-text">
                {isLogoZone ? 'Logo zone' : 'Text zone'} <span className="font-mono text-muted">· {selSlot}</span>
              </div>

              {isTextZone && (
                <>
                  <label className="mb-1 block text-[10.5px] text-muted">Role</label>
                  <select value={selSlot} onChange={(e) => changeRole(e.target.value)} className="mb-2 h-8 w-full rounded-sm border border-border bg-white px-1.5 text-[11px]">
                    {SLOT_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  <div className="grid grid-cols-2 gap-1.5">
                    <select value={selFont} onChange={(e) => applyFont(e.target.value)} title="Font" className="h-8 rounded-sm border border-border bg-white px-1.5 text-[11px]">
                      {INSTALLED_FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <select value={selEffect} onChange={(e) => applyEffect(e.target.value)} title="Text effect" className="h-8 rounded-sm border border-border bg-white px-1.5 text-[11px]">
                      {EFFECTS.map((ef) => <option key={ef.value} value={ef.value}>{ef.label}</option>)}
                    </select>
                  </div>

                  {isArch && (
                    <div className="mt-2">
                      <label className="mb-1 block text-[10.5px] text-muted">Arch apex: {curArchCenter.toFixed(2)} <span className="text-subtle">(0 = left · 0.5 = center · 1 = right)</span></label>
                      <input type="range" min={0} max={1} step={0.05} value={curArchCenter} onChange={(e) => applyArchCenter(Number(e.target.value))} className="w-full" />
                    </div>
                  )}

                  <div className="mb-1 mt-3 text-[10.5px] text-muted">Color slot</div>
                  <div className="grid grid-cols-4 gap-1.5">
                    {COLOR_SLOTS.map((s) => (
                      <button key={s.value} onClick={() => applyColor(s.value)} className={`rounded-sm border px-1 py-1.5 text-[10.5px] ${selRole === s.value ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-white hover:bg-bg'}`}>{s.value}</button>
                    ))}
                  </div>

                  <div className="mt-3 border-t border-border pt-2">
                    <div className="mb-1 flex items-center justify-between text-[10.5px] text-muted">
                      <span>Outlines (stacked: outer → inner)</span>
                      <button onClick={addStroke} className="rounded-sm border border-border bg-white px-1.5 py-0.5 text-[10.5px] hover:bg-bg">+ Add</button>
                    </div>
                    {curStrokes.length === 0 && <div className="text-[10px] text-subtle">No outline yet — add one to stack a colored stroke under the fill.</div>}
                    {curStrokes.map((st, i) => (
                      <div key={i} className="mb-1 flex items-center gap-1.5">
                        <select value={st.color} onChange={(e) => updateStroke(i, { color: e.target.value })} className="h-7 flex-1 rounded-sm border border-border bg-white px-1 text-[11px]">
                          {COLOR_SLOTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                          <option value="transparent">Transparent</option>
                        </select>
                        <input type="number" min={1} max={80} value={st.width} title="Stroke width" onChange={(e) => updateStroke(i, { width: Number(e.target.value) || 1 })} className="h-7 w-14 rounded-sm border border-border bg-white px-1 text-[11px]" />
                        <button onClick={() => removeStroke(i)} title="Remove outline" className="rounded-sm border border-border bg-white px-1.5 py-0.5 text-[10.5px] text-danger hover:bg-danger/5">✕</button>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3">
                    <label className="mb-1 block text-[10.5px] text-muted">Tracking (letter-spacing)</label>
                    <input type="number" value={selTracking} onChange={(e) => applyTracking(Number(e.target.value) || 0)} className="h-7 w-full rounded-sm border border-border bg-white px-1.5 text-[11px]" />
                  </div>
                </>
              )}

              {isLogoZone && (
                <div>
                  <label className="mb-1 block text-[10.5px] text-muted">Padding (inset inside the box)</label>
                  <input type="number" min={0} value={selPad} onChange={(e) => applyPad(Number(e.target.value) || 0)} className="h-7 w-full rounded-sm border border-border bg-white px-1.5 text-[11px]" />
                </div>
              )}

              {/* Anchor 3×3 (both kinds) — dot sits in the cell corner that the code names */}
              <div className="mb-1 mt-3 text-[10.5px] text-muted">Anchor (where content sits in the zone)</div>
              <div className="grid w-[96px] grid-cols-3 gap-1">
                {ANCHORS.map((a) => {
                  const active = selAnchor === a.code;
                  const justify = a.code[1] === 'l' ? 'justify-start' : a.code[1] === 'r' ? 'justify-end' : 'justify-center';
                  const align = a.code[0] === 't' ? 'items-start' : a.code[0] === 'b' ? 'items-end' : 'items-center';
                  return (
                    <button key={a.code} title={a.title} onClick={() => applyAnchor(a.code)} className={`flex h-7 rounded-sm border p-1 ${justify} ${align} ${active ? 'border-primary bg-primary/10' : 'border-border bg-white hover:bg-bg'}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-primary' : 'bg-muted'}`} />
                    </button>
                  );
                })}
              </div>

              <div className="mt-3 flex gap-1.5">
                <button onClick={del} className="flex-1 rounded-sm border border-danger/40 bg-white px-2 py-1.5 text-[11px] text-danger hover:bg-danger/5">Delete</button>
                <button onClick={undo} className="flex-1 rounded-sm border border-border bg-white px-2 py-1.5 text-[11px] hover:bg-bg">↶ Undo</button>
                <button onClick={reset} className="flex-1 rounded-sm border border-border bg-white px-2 py-1.5 text-[11px] hover:bg-bg">Reset</button>
              </div>
            </div>
          )}

          {/* Raw artwork element selected (not yet a zone) → offer snap-to-zone */}
          {hasSvg && selColorClicked && (
            <div className="rounded-md bg-surface p-4 shadow-card">
              <div className="mb-1 text-[11px] font-semibold text-text">Selected artwork element</div>
              <div className="mb-2 text-[10px] text-subtle">Convert it into a text zone at its bounding box (uses the new-zone defaults above), or just map its color in the Colors panel.</div>
              <div className="flex gap-1.5">
                <button onClick={snapSelectionToZone} className="flex-1 rounded-sm border border-border bg-white px-2 py-1.5 text-[11px] hover:bg-bg">Use selection as zone</button>
                <button onClick={del} className="rounded-sm border border-danger/40 bg-white px-2 py-1.5 text-[11px] text-danger hover:bg-danger/5">Delete</button>
              </div>
            </div>
          )}

          {/* Layout (multi-select) */}
          {hasSvg && multi.length >= 1 && (
            <div className="rounded-md bg-surface p-4 shadow-card">
              <div className="mb-1 text-[11px] font-semibold text-text">Layout <span className="text-muted">· {multi.length} selected</span></div>
              <div className="mb-2 text-[10px] text-subtle">Shift-click zones (or use the checkboxes below) to multi-select. Center H/V centers on the {r ? canvasSize(r) : 1000}-unit canvas.</div>

              <div className="mb-1 text-[10.5px] text-muted">Align horizontally</div>
              <div className="mb-2 grid grid-cols-3 gap-1.5">
                <button onClick={() => doAlign('left')} className="rounded-sm border border-border bg-white px-1 py-1.5 text-[10.5px] hover:bg-bg">Left</button>
                <button onClick={() => doAlign('center-h')} className="rounded-sm border border-border bg-white px-1 py-1.5 text-[10.5px] hover:bg-bg">Center</button>
                <button onClick={() => doAlign('right')} className="rounded-sm border border-border bg-white px-1 py-1.5 text-[10.5px] hover:bg-bg">Right</button>
              </div>
              <div className="mb-1 text-[10.5px] text-muted">Align vertically</div>
              <div className="mb-2 grid grid-cols-3 gap-1.5">
                <button onClick={() => doAlign('top')} className="rounded-sm border border-border bg-white px-1 py-1.5 text-[10.5px] hover:bg-bg">Top</button>
                <button onClick={() => doAlign('center-v')} className="rounded-sm border border-border bg-white px-1 py-1.5 text-[10.5px] hover:bg-bg">Middle</button>
                <button onClick={() => doAlign('bottom')} className="rounded-sm border border-border bg-white px-1 py-1.5 text-[10.5px] hover:bg-bg">Bottom</button>
              </div>
              <div className="mb-1 text-[10.5px] text-muted">Center on canvas</div>
              <div className="mb-2 grid grid-cols-2 gap-1.5">
                <button onClick={() => doAlign('center-h')} className="rounded-sm border border-border bg-white px-1 py-1.5 text-[10.5px] hover:bg-bg">Center H</button>
                <button onClick={() => doAlign('center-v')} className="rounded-sm border border-border bg-white px-1 py-1.5 text-[10.5px] hover:bg-bg">Center V</button>
              </div>

              <div className="mb-1 flex items-center justify-between text-[10.5px] text-muted">
                <span>Distribute</span>
                <div className="inline-flex overflow-hidden rounded-sm border border-border">
                  <button onClick={() => setDistMode('gaps')} className={`px-2 py-0.5 text-[10px] ${distMode === 'gaps' ? 'bg-primary text-white' : 'bg-white hover:bg-bg'}`}>Gaps</button>
                  <button onClick={() => setDistMode('centers')} className={`px-2 py-0.5 text-[10px] ${distMode === 'centers' ? 'bg-primary text-white' : 'bg-white hover:bg-bg'}`}>Centers</button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <button disabled={multi.length < 3} onClick={() => doDistribute('h')} className="rounded-sm border border-border bg-white px-1 py-1.5 text-[10.5px] hover:bg-bg disabled:opacity-40">Distribute H</button>
                <button disabled={multi.length < 3} onClick={() => doDistribute('v')} className="rounded-sm border border-border bg-white px-1 py-1.5 text-[10.5px] hover:bg-bg disabled:opacity-40">Distribute V</button>
              </div>
              {multi.length < 3 && <div className="mt-1 text-[10px] text-subtle">Distribute needs 3+ zones selected.</div>}
            </div>
          )}

          {/* Zones list */}
          {hasSvg && (
            <div className="rounded-md bg-surface p-4 shadow-card">
              <div className="mb-2 text-[11px] font-semibold text-text">Zones <span className="text-muted">· {zones.length}</span></div>
              {zones.length === 0 && <div className="text-[10px] text-subtle">No zones yet — draw a Text or Logo zone on the artwork.</div>}
              <div className="space-y-1">
                {zones.map((z, i) => {
                  const sel = z.el === selected;
                  const inMulti = multi.includes(z.el);
                  return (
                    <div key={i} className={`flex items-center gap-1.5 rounded-sm border px-1.5 py-1 ${sel ? 'border-primary bg-primary/5' : 'border-border bg-white'}`}>
                      <input type="checkbox" checked={inMulti} onChange={() => selectZone(z.el, true)} title="Multi-select" className="h-3.5 w-3.5" />
                      <button onClick={() => selectZone(z.el)} className="flex flex-1 items-center gap-1.5 text-left">
                        <span className={`rounded-sm px-1.5 py-0.5 text-[10px] ${z.kind === 'logo' ? 'bg-primary/10 text-primary' : 'bg-success/10 text-success'}`}>{z.role}</span>
                        <span className="text-[10px] text-subtle">{z.kind}</span>
                      </button>
                      <button onClick={() => deleteZone(z.el)} title="Delete zone" className="rounded-sm border border-border bg-white px-1.5 py-0.5 text-[10.5px] text-danger hover:bg-danger/5">✕</button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Save card */}
          {hasSvg && (
            <div className="rounded-md bg-surface p-4 shadow-card">
              <label className="mb-1 block text-[11px] text-muted">Template ID</label>
              <Input value={id} onChange={(e) => setId(e.target.value)} placeholder="e.g. camelot-dragons" />
              <label className="mb-1 mt-3 block text-[11px] text-muted">Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Camelot Dragons" />
              <label className="mb-1 mt-3 block text-[11px] text-muted">Sport</label>
              <Input value={sport} onChange={(e) => setSport(e.target.value)} placeholder="football" />
              <div className="mt-4 flex gap-2">
                <button onClick={doPreview} className="flex-1 rounded-sm border border-border bg-white px-3 py-2 text-[12px] hover:bg-bg">Preview</button>
                <button onClick={save} className="flex-1 rounded-sm bg-primary px-3 py-2 text-[12px] font-medium text-white hover:opacity-90">Save template</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
